import type { ChatItem, MessageGroup } from "../types/chat-types.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import {
  buildMinimalAssistStatusLabel,
  isFleetCompletionMessage,
  resolveAssistDisplay,
  shouldIncludeMessageInMinimalAssist,
} from "./chat-assist-display.ts";
import { buildAssistantTurns } from "./chat-assistant-turn.ts";
import { isFileChangesMessage, parseFileChangesPayload } from "./chat-file-changes-summary.ts";
import { resolveChatHistoryPaging } from "./chat-history-paging.ts";
import { sessionChildren } from "./chat-session-children.ts";
import { buildSearchRegex } from "./chat-thread-search-highlight.ts";
import {
  extractBackgroundTaskResultBody,
  extractBackgroundTaskResultLabel,
  isOperatorFacingInjectText,
  patchUserMessageForDisplay,
  shouldOmitChatTranscriptMessage,
  shouldOmitChatToolTranscriptMessage,
  stripOrchestrationBlocksFromAssistantReplies,
} from "./chat-transcript-filters.ts";
import type { ChatProps } from "./chat-view-props.ts";
import { shouldShowLiveRunIndicator } from "./chat-wait-elapsed.ts";
import {
  fleetCompleteHeadline,
  parseFleetCompletionBody,
  stripFirstMatchingLine,
  stripFleetCompleteLabel,
} from "./fleet-completion-notice.ts";
import { extractText } from "./message-extract.ts";
import { normalizeMessage, normalizeRoleForGrouping } from "./message-normalizer.ts";
import { parsePlanApproval } from "./plan-approval-notice.ts";
import { isSubagentRunCard } from "./subagent-run-display.ts";
import { isSubagentRunActive } from "./subagent-run-status.ts";

function groupMessages(items: ChatItem[]): Array<ChatItem | MessageGroup> {
  const result: Array<ChatItem | MessageGroup> = [];
  let currentGroup: MessageGroup | null = null;

  for (const item of items) {
    if (item.kind !== "message") {
      if (currentGroup) {
        result.push(currentGroup);
        currentGroup = null;
      }
      result.push(item);
      continue;
    }

    const normalized = normalizeMessage(item.message);
    const role = normalizeRoleForGrouping(normalized.role);
    const timestamp = normalized.timestamp || 0;

    if (!currentGroup || currentGroup.role !== role) {
      if (currentGroup) {
        result.push(currentGroup);
      }
      currentGroup = {
        kind: "group",
        key: `group:${role}:${item.key}`,
        role,
        messages: [{ message: item.message, key: item.key }],
        timestamp,
        isStreaming: false,
      };
    } else {
      currentGroup.messages.push({ message: item.message, key: item.key });
    }
  }

  if (currentGroup) {
    result.push(currentGroup);
  }
  return result;
}

function hasStreamingTailInHistory(history: readonly unknown[], runId: string): boolean {
  if (!runId || typeof runId !== "string") {
    return false;
  }
  const trimmed = runId.trim();
  if (!trimmed) {
    return false;
  }
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const msg = history[i];
    if (!msg || typeof msg !== "object") {
      continue;
    }
    const m = msg as { streaming?: unknown; role?: unknown; id?: unknown; runId?: unknown };
    if (m.streaming === true && m.role === "assistant") {
      const candidate =
        typeof m.runId === "string" ? m.runId : typeof m.id === "string" ? m.id : "";
      if (candidate === trimmed) {
        return true;
      }
    }
  }
  return false;
}

function messageKey(message: unknown, index: number): string {
  const m = message as Record<string, unknown>;
  const toolCallId = typeof m.toolCallId === "string" ? m.toolCallId : "";
  if (toolCallId) {
    return `tool:${toolCallId}`;
  }
  const flatId = typeof m.id === "string" ? m.id : "";
  const messageId = typeof m.messageId === "string" ? m.messageId : "";
  const transcriptId = typeof m.transcriptId === "string" ? m.transcriptId : "";
  const inner =
    m.type === "message" && m.message && typeof m.message === "object"
      ? (m.message as Record<string, unknown>)
      : null;
  const innerId = inner ? (typeof inner.id === "string" ? inner.id : "") : "";
  const id = flatId || messageId || transcriptId || innerId;
  if (id) {
    return `msg:${id}`;
  }
  const timestamp = typeof m.timestamp === "number" ? m.timestamp : null;
  const role = typeof m.role === "string" ? m.role : "unknown";
  if (timestamp != null) {
    return `msg:${role}:${timestamp}:${index}`;
  }
  return `msg:${role}:${index}`;
}

/**
 * Collapse consecutive subagent result dumps into one `subagent-results`
 * section, in place.
 *
 * Each child that finishes injects its full output into the parent
 * transcript. Rendered as individual bubbles those dumps bury the parent's
 * own messages — and the final synthesis — under a wall of background text,
 * which is exactly what the operator complained about. Only *runs* of
 * consecutive results are grouped into one counted section: a result
 * separated from the next by a real parent message keeps its own slot, so
 * chronological reading is preserved and a synthesis that lands after a
 * burst is never pulled into the group.
 *
 * A lone result is still a `subagent-results` group — never a bare
 * `fleet-result`. Serial plans (one unit at a time) deliver their reports
 * separated by parent messages, so almost every report arrives alone and
 * used to render fully expanded, which is precisely the wall of text this
 * function exists to prevent. The renderer drops the outer section wrapper
 * for a one-item group, so the operator gets one collapsed bubble per
 * report instead of a one-entry section nested inside another disclosure.
 */
export function mergeConsecutiveSubagentResults(items: ChatItem[]): void {
  let out = 0;
  let i = 0;
  while (i < items.length) {
    const item = items[i];
    if (item?.kind !== "fleet-result") {
      items[out] = item as ChatItem;
      out += 1;
      i += 1;
      continue;
    }
    // Start of a run: consume every immediately-following fleet-result.
    const first = item;
    const results: Array<{
      key: string;
      body: string;
      timestamp: number;
      label?: string;
    }> = [];
    while (i < items.length && items[i]?.kind === "fleet-result") {
      const r = items[i] as Extract<ChatItem, { kind: "fleet-result" }>;
      results.push({
        key: r.key,
        body: r.body,
        timestamp: r.timestamp,
        ...(r.label ? { label: r.label } : {}),
      });
      i += 1;
    }
    items[out] = {
      kind: "subagent-results",
      key: `subagent-results:${first.key}`,
      results,
      timestamp: results[0]?.timestamp ?? first.timestamp,
    };
    out += 1;
  }
  items.length = out;
}

export function buildChatItems(props: ChatProps): Array<ChatItem | MessageGroup> {
  const assistDisplay = resolveAssistDisplay(props);
  const showThinking = assistDisplay === "on";
  const minimal = assistDisplay === "minimal";
  const tools = (Array.isArray(props.toolMessages) ? props.toolMessages : []).filter(
    (tool) => !shouldOmitChatToolTranscriptMessage(tool),
  );
  const { history } = resolveChatHistoryPaging(props);
  const searchQRaw = props.chatMessageSearchQuery ?? "";
  const searchRegex = buildSearchRegex(searchQRaw);
  const effectiveSearch = searchRegex !== null;

  type SortEntry = { at: number; item: ChatItem; _origIndex?: number };
  const sortable: SortEntry[] = [];

  for (let i = 0; i < history.length; i++) {
    const msg = history[i];
    const normalized = normalizeMessage(msg);

    if (effectiveSearch) {
      const text = extractText(msg) ?? "";
      if (!text) continue;
      const normalizedText = text.toLowerCase().replace(/\s+/g, " ");
      searchRegex.lastIndex = 0;
      if (!searchRegex.test(normalizedText)) {
        continue;
      }
    }

    if (minimal) {
      if (!shouldIncludeMessageInMinimalAssist(msg)) {
        continue;
      }
    } else if (!showThinking && normalized.role.toLowerCase() === "toolresult") {
      continue;
    }

    if (shouldOmitChatTranscriptMessage(msg)) {
      continue;
    }

    const role = normalized.role.toLowerCase();
    const orchestrationStripped =
      role === "assistant"
        ? (stripOrchestrationBlocksFromAssistantReplies([
            msg as { role?: string; content?: unknown; text?: unknown },
          ])[0] ?? msg)
        : msg;
    const displayMsg = role === "user" ? patchUserMessageForDisplay(msg) : orchestrationStripped;

    const ts = normalized.timestamp || 0;
    const fileChangesPayload = isFileChangesMessage(displayMsg)
      ? parseFileChangesPayload(displayMsg)
      : null;
    if (fileChangesPayload) {
      sortable.push({
        at: ts,
        item: {
          kind: "file-changes",
          key: `file-changes:${messageKey(msg, i)}`,
          payload: fileChangesPayload,
          timestamp: ts,
          sessionKey: props.sessionKey,
        },
      });
      continue;
    }

    const rawText = extractText(displayMsg) ?? "";
    if (isFleetCompletionMessage(displayMsg)) {
      const stripped = stripFleetCompleteLabel(rawText);
      const headline = fleetCompleteHeadline(stripped);
      // The headline is already lifted into the styled header. Echoing it as
      // the first line of the body made the bubble read as a duplicated,
      // unfinished sentence — the operator-visible "still looking bad" of
      // the latest synthesis runs. Drop the matching line from the body so
      // the body starts at the first line of *new* content.
      const body = stripFirstMatchingLine(stripped, headline);
      // Parse into a structured final state (status counts, goal, per-unit
      // findings, artifacts, failed/cancelled titles) so the renderer can
      // build a real completion card instead of a monospace run-on.
      const state = parseFleetCompletionBody(stripped);
      sortable.push({
        at: ts,
        item: {
          kind: "fleet-complete",
          key: `fleet-complete:${messageKey(msg, i)}`,
          headline,
          body,
          state,
          timestamp: ts,
        },
      });
      continue;
    }

    const planApproval = role === "assistant" ? parsePlanApproval(rawText) : null;
    if (planApproval) {
      // The plan approval is a status event, not prose. Left in the
      // generic assistant bubble it read as one more wall of monospace —
      // the least legible moment in the thread. Give it its own item so
      // the renderer can show it as a card.
      sortable.push({
        at: ts,
        item: {
          kind: "plan-approval",
          key: `plan-approval:${messageKey(msg, i)}`,
          ...planApproval,
          timestamp: ts,
        },
      });
      continue;
    }

    const resultBody = isOperatorFacingInjectText(rawText)
      ? extractBackgroundTaskResultBody(rawText)
      : role === "user"
        ? extractBackgroundTaskResultBody(rawText)
        : null;
    if (resultBody) {
      const resultLabel = extractBackgroundTaskResultLabel(rawText);
      sortable.push({
        at: ts,
        item: {
          kind: "fleet-result",
          key: `fleet-result:${messageKey(msg, i)}`,
          body: resultBody,
          timestamp: ts,
          ...(resultLabel ? { label: resultLabel } : {}),
        },
      });
      continue;
    }
    sortable.push({
      at: ts,
      item: {
        kind: "message",
        key: messageKey(msg, i),
        message: displayMsg,
      },
    });
  }

  const filt = props.chatSubagentFilter ?? "all";

  const filtActive = (c: ChatSubagentCard) => filt !== "running" || isSubagentRunActive(c.status);

  const groupedByOrchestration = new Map<string, ChatSubagentCard[]>();
  const threadSubagents = sessionChildren(props).filter(isSubagentRunCard);

  if (!minimal) {
    for (const c of threadSubagents) {
      if (c.orchestrationId) {
        const list = groupedByOrchestration.get(c.orchestrationId) ?? [];
        list.push(c);
        groupedByOrchestration.set(c.orchestrationId, list);
      }
    }

    for (const [orchestrationId, cards] of groupedByOrchestration) {
      const activeCards = cards.filter(filtActive);
      if (activeCards.length === 0) continue;
      sortable.push({
        at: activeCards[0].createdAt ?? 0,
        item: {
          kind: "orchestration-group",
          key: `orchestration:${orchestrationId}`,
          orchestrationId,
          cardIds: cards.map((c) => c.id),
        },
      });
    }

    for (const c of threadSubagents) {
      if (c.orchestrationId) continue;
      if (!filtActive(c)) continue;
      sortable.push({
        at: c.createdAt ?? 0,
        item: {
          kind: "subagent-run",
          key: `subagent:${c.id}`,
          cardId: c.id,
        },
      });
    }
  }

  sortable.forEach((entry, idx) => {
    entry._origIndex = idx;
  });
  sortable.sort((a, b) => {
    if (a.at !== b.at) {
      return a.at - b.at;
    }
    return (a._origIndex ?? 0) - (b._origIndex ?? 0);
  });
  for (const entry of sortable) {
    delete entry._origIndex;
  }

  const items: ChatItem[] = [];
  for (const s of sortable) {
    items.push(s.item);
  }
  mergeConsecutiveSubagentResults(items);

  const historyToolCallIds = new Set<string>();
  for (const msg of history) {
    const m = msg as Record<string, unknown>;
    const toolCallId =
      typeof m.toolCallId === "string"
        ? m.toolCallId
        : typeof m.tool_call_id === "string"
          ? m.tool_call_id
          : "";
    if (toolCallId) {
      historyToolCallIds.add(toolCallId);
    }
  }

  const isLive = shouldShowLiveRunIndicator({
    sending: Boolean(props.sending),
    canAbort: props.canAbort,
    stream: props.stream,
    streamStartedAt: props.streamStartedAt ?? null,
    nowMs: props.chatWaitNow ?? Date.now(),
    toolMessageCount: tools.length,
    injectedAt: props.injectedAt ?? null,
  });

  if (!minimal) {
    if (isLive) {
      for (let i = 0; i < tools.length; i++) {
        const tool = tools[i] as Record<string, unknown>;
        const toolCallId =
          typeof tool.toolCallId === "string"
            ? tool.toolCallId
            : typeof tool.tool_call_id === "string"
              ? tool.tool_call_id
              : "";
        if (toolCallId && historyToolCallIds.has(toolCallId)) {
          continue;
        }
        items.push({
          kind: "message",
          key: `live-tool:${messageKey(tool, i + history.length)}`,
          message: tool,
        });
      }
    }

    const transcriptOwnsStream =
      typeof props.chatRunId === "string" &&
      props.chatRunId.trim().length > 0 &&
      hasStreamingTailInHistory(history, props.chatRunId);
    if (!transcriptOwnsStream) {
      if (isLive) {
        const key = `live:${props.sessionKey}:${props.streamStartedAt ?? "pending"}`;
        items.push({
          kind: "live-run",
          key,
          streamText: props.stream ?? "",
          reasoningStreamText: props.reasoningStream ?? "",
          startedAt: props.streamStartedAt ?? Date.now(),
        });
      } else if (typeof props.stream === "string" && props.stream.trim().length > 0) {
        const key = `stream:${props.sessionKey}:${props.streamStartedAt ?? "live"}`;
        items.push({
          kind: "stream",
          key,
          text: props.stream,
          startedAt: props.streamStartedAt ?? Date.now(),
        });
      }
    }
  } else if (isLive) {
    const statusLabel = buildMinimalAssistStatusLabel({
      runActive: isLive,
      subagents: sessionChildren(props),
      liveRunHint: props.liveRunHint,
    });
    if (statusLabel) {
      items.push({
        kind: "minimal-status",
        key: `minimal-status:${props.sessionKey}:${props.streamStartedAt ?? "pending"}`,
        label: statusLabel,
        startedAt: props.streamStartedAt ?? Date.now(),
      });
    }
  }

  if (props.chatHistoryLoadingOlder === true) {
    items.unshift({
      kind: "older-loading-indicator",
      key: "older-loading-indicator",
    });
  }
  const grouped = groupMessages(items);
  if (minimal) {
    return grouped;
  }
  return buildAssistantTurns(grouped, {
    isLive,
    currentRunId: props.chatRunId ?? null,
  });
}
